import * as React from "react";
import copy from "copy-to-clipboard";
import { Palette, Copy, Check, Smile, Captions, Film, Music, Sparkles } from "lucide-react";
import { cn } from "@/lib/utils";
import { StylePreferencesOptions } from "@/utils/projectHelpers";

interface StylePreferencesProps {
  preferencesInfo: StylePreferencesOptions[];
  formatHexColor: (color: string) => string | null;
}

export const StylePreferencesComponent: React.FC<StylePreferencesProps> = ({ preferencesInfo, formatHexColor }) => {
  const [copiedText, setCopiedText] = React.useState<string | undefined>(undefined);

  const copyToClipboard = (text: string) => {
    copy(text);
    setCopiedText(text);
    setTimeout(() => {
      setCopiedText(undefined);
    }, 2000);
  };

  const getPreferenceIcon = (key: string) => {
    const lowerKey = key.toLowerCase();
    if (lowerKey.includes("emoji")) {
      return <Smile className="size-3.5" />;
    }
    if (lowerKey.includes("caption")) {
      return <Captions className="size-3.5" />;
    }
    if (lowerKey.includes("clip") || lowerKey.includes("video") || lowerKey.includes("broll")) {
      return <Film className="size-3.5" />;
    }
    if (lowerKey.includes("music")) {
      return <Music className="size-3.5" />;
    }
    if (lowerKey.includes("tone")) {
      return <Sparkles className="size-3.5" />;
    }
    return <Sparkles className="size-3.5" />;
  };

  const { textPreferences, colorPreferences } = React.useMemo(() => {
    const texts: StylePreferencesOptions[] = [];
    const colors: StylePreferencesOptions[] = [];
    preferencesInfo.forEach(pref => {
      if (pref.colors && pref.colors.length > 0) {
        colors.push(pref);
      } else {
        texts.push(pref);
      }
    });
    return { textPreferences: texts, colorPreferences: colors };
  }, [preferencesInfo]);

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-row justify-between items-center w-full select-none">
        <label className="sectionHeader font-semibold text-slate-800 text-sm flex items-center gap-1.5">
          <Palette className="size-4.5 text-indigo-600" />
          Style Preferences
        </label>
      </div>
      <div className="videoProjectContainer flex flex-col gap-4 bg-[#f8faff] p-5 rounded-xl border border-indigo-150 border-l-4 border-l-indigo-500 shadow-xs">
        <div className="flex flex-col gap-4 text-sm text-slate-600">
          {preferencesInfo.length > 0 ? (
            <>
              {textPreferences.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-1">
                  {textPreferences.map(pref => {
                    const isNo = pref.key.toLowerCase().startsWith("no ");
                    return (
                      <span
                        key={pref.key}
                        className={cn(
                          "inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border select-none transition-colors",
                          isNo
                            ? "bg-slate-50 text-slate-400 border-slate-200"
                            : "bg-indigo-50/50 text-indigo-700 border-indigo-100"
                        )}
                      >
                        <span
                          className={cn(
                            "shrink-0 flex items-center justify-center",
                            isNo ? "text-slate-300" : "text-indigo-500"
                          )}
                        >
                          {getPreferenceIcon(pref.key)}
                        </span>
                        {pref.key}
                      </span>
                    );
                  })}
                </div>
              )}

              {colorPreferences.length > 0 && (
                <div
                  className={cn(
                    "flex flex-col gap-3.5",
                    textPreferences.length > 0 && "pt-3.5 border-t border-indigo-100/50"
                  )}
                >
                  {colorPreferences.map(preference => (
                    <div key={preference.key} className="flex flex-col gap-2">
                      <span className="font-bold text-slate-700 text-xs uppercase tracking-wider">
                        {preference.key}
                      </span>
                      <div className="grid grid-cols-2 gap-2.5">
                        {preference.colors?.map((color, index) => {
                          const hex = formatHexColor(color) || "";
                          return (
                            <div
                              key={`${color}-${index}`}
                              onClick={() => copyToClipboard(hex)}
                              className={cn(
                                "group/swatch relative flex flex-row items-center justify-between p-2.5 gap-2 rounded-lg bg-white border border-slate-200 hover:border-indigo-300 hover:bg-indigo-50/5 hover:shadow-xs transition-all cursor-pointer select-none w-full",
                                copiedText === hex &&
                                  "border-emerald-300 bg-emerald-50/10 hover:border-emerald-300 hover:bg-emerald-50/10"
                              )}
                            >
                              <div className="flex flex-row items-center gap-2.5 min-w-0">
                                <div
                                  style={{ backgroundColor: color }}
                                  className="size-7 rounded-md border border-slate-200 shadow-3xs shrink-0 group-hover/swatch:scale-105 transition-transform duration-200"
                                />
                                <div className="flex flex-col text-left min-w-0">
                                  <span className="font-mono text-xs font-bold text-slate-800 tracking-wider truncate">
                                    {hex.toUpperCase()}
                                  </span>
                                  <span
                                    className={cn(
                                      "text-xs transition-colors truncate",
                                      copiedText === hex
                                        ? "text-emerald-600 font-medium"
                                        : "text-slate-400 group-hover/swatch:text-indigo-600"
                                    )}
                                  >
                                    {copiedText === hex ? "Copied!" : "Click to copy"}
                                  </span>
                                </div>
                              </div>
                              <div className="shrink-0 flex items-center justify-center">
                                {copiedText === hex ? (
                                  <Check className="size-3.5 text-emerald-600 animate-bounce" />
                                ) : (
                                  <Copy className="size-3 text-slate-400 group-hover/swatch:text-indigo-500 transition-colors" />
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          ) : (
            <div className="text-slate-400 text-xs py-2 text-center select-none">No style preferences set.</div>
          )}
        </div>
      </div>
    </div>
  );
};
